import { useState, useEffect, useRef } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Button } from "@/components/ui/button";
import { Video, Mic, MicOff, VideoOff, PhoneOff } from "lucide-react";

export default function VideoCall({ roomUrl, token, userName, isOwner, onClose }) {
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const [status, setStatus] = useState("Connecting…");
  const [joined, setJoined] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let localFrame = null;
    (async () => {
      // StrictMode/dev double-mounts can leave a lingering singleton; destroy it first.
      const existing = DailyIframe.getCallInstance?.();
      if (existing) { try { await existing.destroy(); } catch {} }
      if (cancelled || !containerRef.current) return;
      localFrame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: false, showFullscreenButton: true,
        iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "8px" },
      });
      frameRef.current = localFrame;
      localFrame.on("joined-meeting", () => { setJoined(true); setStatus("Connected"); });
      localFrame.on("left-meeting", () => { setStatus("Disconnected"); });
      localFrame.on("error", (e) => { console.error("Daily error", e); setStatus("Error"); });
      localFrame.join({ url: roomUrl, token, userName }).catch((err) => console.error("join failed", err));
    })();
    return () => {
      cancelled = true;
      const f = frameRef.current || localFrame;
      if (f) { try { f.destroy(); } catch {} }
      frameRef.current = null;
    };
  }, [roomUrl, token, userName]);

  const toggleCamera = async () => { if (frameRef.current && joined) { await frameRef.current.setLocalVideo(!cameraOn); setCameraOn(!cameraOn); } };
  const toggleMic = async () => { if (frameRef.current && joined) { await frameRef.current.setLocalAudio(!micOn); setMicOn(!micOn); } };
  const leave = async () => { try { await frameRef.current?.leave(); frameRef.current?.destroy(); } catch {} frameRef.current = null; onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-zinc-900 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 text-white text-sm">
          <span className="flex items-center gap-2"><Video className="w-4 h-4 text-cc-green" /> {status}</span>
        </div>
        <div ref={containerRef} style={{ height: 480, background: "#0f172a" }} />
        <div className="flex items-center justify-center gap-3 py-4 bg-zinc-900">
          <Button size="icon" variant={micOn ? "secondary" : "destructive"} onClick={toggleMic} data-testid="toggle-mic">{micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}</Button>
          <Button size="icon" variant={cameraOn ? "secondary" : "destructive"} onClick={toggleCamera} data-testid="toggle-camera">{cameraOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}</Button>
          <Button onClick={leave} className="bg-red-600 hover:bg-red-700 text-white" data-testid="leave-call"><PhoneOff className="w-4 h-4 mr-2" />{isOwner ? "End for all" : "Leave"}</Button>
        </div>
      </div>
    </div>
  );
}
